import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { BaseToastAlertClasses } from '../../shared/enums/base-toast-alert-classes.enum';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { baseLocationWorkingTimeActions, baseSharedActions } from '../actions';
import { BaseCoreState } from '../reducers';
import { baseAuthSelectors } from '../selectors';
import { BaseCustomerLocationService } from '../../shared/services/base-customer-location.service';
import { BaseHttpErrorService } from '../../core/services/base-http-error.service';

@Injectable()
export class BaseLocationWorkingTimeEffects {
  loadDutyModels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.loadDutyModels),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.locationService
          .getDutyModels({
            customer: user.organizationId,
            location: action.payload.location,
          })
          .pipe(
            mergeMap((response) => [
              baseLocationWorkingTimeActions.setDutyModels({ payload: response }),
              baseLocationWorkingTimeActions.successDutyModels(),
            ]),
            catchError(() => of(baseLocationWorkingTimeActions.errorDutyModels()))
          );
      })
    );
  });

  loadBreakRules$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.loadBreakRules),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.locationService
          .getBreakRules({
            customer: user.organizationId,
            location: action.payload.location,
          })
          .pipe(
            mergeMap((response) => [
              baseLocationWorkingTimeActions.setBreakRules({ payload: response }),
              baseLocationWorkingTimeActions.successBreakRules(),
            ]),
            catchError(() => of(baseLocationWorkingTimeActions.errorBreakRules()))
          );
      })
    );
  });

  createDutyModel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.createDutyModel),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      concatMap(([action, user]) => {
        return this.locationService
          .createDutyModel({
            customer: user.organizationId,
            location: action.payload.location,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => this.dutyModelSaved(action.payload.location)),
            catchError((error: HttpErrorResponse) => this.dutyModelSaveFailed(error))
          );
      })
    );
  });

  updateDutyModel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.updateDutyModel),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      concatMap(([action, user]) => {
        return this.locationService
          .updateDutyModel({
            customer: user.organizationId,
            location: action.payload.location,
            dutyModel: action.payload.dutyModel,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => this.dutyModelSaved(action.payload.location)),
            catchError((error: HttpErrorResponse) => this.dutyModelSaveFailed(error))
          );
      })
    );
  });

  deleteDutyModel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.deleteDutyModel),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      mergeMap(([action, user]) => {
        return this.locationService
          .deleteDutyModel({
            customer: user.organizationId,
            location: action.payload.location,
            dutyModel: action.payload.dutyModel,
          })
          .pipe(
            mergeMap(() => this.dutyModelSaved(action.payload.location)),
            // The backend has no reference gate yet (KS-7074); once it 400s, the interceptor toasts it.
            catchError(() => of(baseLocationWorkingTimeActions.errorDutyModelCrud({})))
          );
      })
    );
  });

  setDutyModelActivation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.setDutyModelActivation),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      mergeMap(([action, user]) => {
        return this.locationService
          .setDutyModelActivation({
            customer: user.organizationId,
            location: action.payload.location,
            dutyModel: action.payload.dutyModel,
            active: action.payload.active,
          })
          .pipe(
            mergeMap(() => this.dutyModelSaved(action.payload.location)),
            // "Already (de)activated" is a 400 from a stale list; the interceptor toasts it and the row
            // keeps its last known state because the reload in dutyModelSaved is skipped.
            catchError(() => of(baseLocationWorkingTimeActions.errorDutyModelCrud({})))
          );
      })
    );
  });

  createBreakRule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.createBreakRule),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      concatMap(([action, user]) => {
        return this.locationService
          .createBreakRule({
            customer: user.organizationId,
            location: action.payload.location,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => this.breakRuleSaved(action.payload.location)),
            catchError(() => of(baseLocationWorkingTimeActions.errorBreakRuleCrud()))
          );
      })
    );
  });

  updateBreakRule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.updateBreakRule),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      concatMap(([action, user]) => {
        return this.locationService
          .updateBreakRule({
            customer: user.organizationId,
            location: action.payload.location,
            breakRule: action.payload.breakRule,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => this.breakRuleSaved(action.payload.location)),
            catchError(() => of(baseLocationWorkingTimeActions.errorBreakRuleCrud()))
          );
      })
    );
  });

  deleteBreakRule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.deleteBreakRule),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      mergeMap(([action, user]) => {
        return this.locationService
          .deleteBreakRule({
            customer: user.organizationId,
            location: action.payload.location,
            breakRule: action.payload.breakRule,
          })
          .pipe(
            mergeMap(() => this.breakRuleSaved(action.payload.location)),
            // A rule referenced by a duty model comes back as a 400; the http-errors interceptor toasts it.
            catchError(() => of(baseLocationWorkingTimeActions.errorBreakRuleCrud()))
          );
      })
    );
  });

  setBreakRuleActivation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.setBreakRuleActivation),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      mergeMap(([action, user]) => {
        return this.locationService
          .setBreakRuleActivation({
            customer: user.organizationId,
            location: action.payload.location,
            breakRule: action.payload.breakRule,
            active: action.payload.active,
          })
          .pipe(
            mergeMap(() => this.breakRuleSaved(action.payload.location)),
            // "Already (de)activated" is a 400 from a stale list; the interceptor toasts it and the
            // reload in breakRuleSaved is skipped, so the row keeps its last known state.
            catchError(() => of(baseLocationWorkingTimeActions.errorBreakRuleCrud()))
          );
      })
    );
  });

  loadRoundingRules$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.loadRoundingRules),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.locationService
          .getRoundingRules({
            customer: user.organizationId,
            location: action.payload.location,
          })
          .pipe(
            mergeMap((response) => [
              baseLocationWorkingTimeActions.setRoundingRules({ payload: response }),
              baseLocationWorkingTimeActions.successRoundingRules(),
            ]),
            catchError(() => of(baseLocationWorkingTimeActions.errorRoundingRules()))
          );
      })
    );
  });

  createRoundingRule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.createRoundingRule),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      concatMap(([action, user]) => {
        return this.locationService
          .createRoundingRule({
            customer: user.organizationId,
            location: action.payload.location,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => this.roundingRuleSaved(action.payload.location)),
            catchError(() => of(baseLocationWorkingTimeActions.errorRoundingRuleCrud()))
          );
      })
    );
  });

  updateRoundingRule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.updateRoundingRule),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      concatMap(([action, user]) => {
        return this.locationService
          .updateRoundingRule({
            customer: user.organizationId,
            location: action.payload.location,
            roundingRule: action.payload.roundingRule,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => this.roundingRuleSaved(action.payload.location)),
            catchError(() => of(baseLocationWorkingTimeActions.errorRoundingRuleCrud()))
          );
      })
    );
  });

  deleteRoundingRule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.deleteRoundingRule),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      mergeMap(([action, user]) => {
        return this.locationService
          .deleteRoundingRule({
            customer: user.organizationId,
            location: action.payload.location,
            roundingRule: action.payload.roundingRule,
          })
          .pipe(
            mergeMap(() => this.roundingRuleSaved(action.payload.location)),
            // A referenced or default rule comes back as a 400; the http-errors interceptor toasts it.
            catchError(() => of(baseLocationWorkingTimeActions.errorRoundingRuleCrud()))
          );
      })
    );
  });

  setRoundingRuleActivation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseLocationWorkingTimeActions.setRoundingRuleActivation),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      mergeMap(([action, user]) => {
        return this.locationService
          .setRoundingRuleActivation({
            customer: user.organizationId,
            location: action.payload.location,
            roundingRule: action.payload.roundingRule,
            active: action.payload.active,
          })
          .pipe(
            mergeMap(() => this.roundingRuleSaved(action.payload.location)),
            // Same as break rules: a stale-list 400 is toasted by the interceptor and the row keeps its state.
            catchError(() => of(baseLocationWorkingTimeActions.errorRoundingRuleCrud()))
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private readonly locationService: BaseCustomerLocationService,
    private readonly httpErrorService: BaseHttpErrorService
  ) {}

  /**
   * Create / update skip the interceptor's toast (see `BaseCustomerLocationService`): a duplicate short code is
   * shown at the form field instead, so only that error goes without a toast — any other failure still gets one.
   */
  private dutyModelSaveFailed(error: HttpErrorResponse) {
    const errors = error.error?.errors;
    const fieldErrors = errors && !Array.isArray(errors) ? (errors as Record<string, string[]>) : undefined;

    if (!fieldErrors?.['shortCode']) {
      this.httpErrorService.handle(error);
    }

    return of(baseLocationWorkingTimeActions.errorDutyModelCrud({ errors: fieldErrors }));
  }

  /** Every duty-model mutation finishes the same way: confirm, refresh the list, tell the user. */
  private dutyModelSaved(location: number) {
    return [
      baseLocationWorkingTimeActions.successDutyModelCrud(),
      baseLocationWorkingTimeActions.loadDutyModels({ payload: { location } }),
      baseSharedActions.addSystemAlert({
        payload: {
          class: BaseToastAlertClasses.success,
          body: 'successMessage.changesSaved',
        },
      }),
    ];
  }

  /** Same shape as `dutyModelSaved`: confirm, refresh the rounding-rule list, tell the user. */
  private roundingRuleSaved(location: number) {
    return [
      baseLocationWorkingTimeActions.successRoundingRuleCrud(),
      baseLocationWorkingTimeActions.loadRoundingRules({ payload: { location } }),
      baseSharedActions.addSystemAlert({
        payload: {
          class: BaseToastAlertClasses.success,
          body: 'successMessage.changesSaved',
        },
      }),
    ];
  }

  /** Same shape as `dutyModelSaved`: confirm, refresh the break-rule list, tell the user. */
  private breakRuleSaved(location: number) {
    return [
      baseLocationWorkingTimeActions.successBreakRuleCrud(),
      baseLocationWorkingTimeActions.loadBreakRules({ payload: { location } }),
      baseSharedActions.addSystemAlert({
        payload: {
          class: BaseToastAlertClasses.success,
          body: 'successMessage.changesSaved',
        },
      }),
    ];
  }
}
